// src/pages/Register.js

import React, { useEffect, useState } from 'react'
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Checkbox,
    FormControlLabel,
    FormHelperText,
} from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'

const countryCurrencyMap = {
    'United States': 'USD',
    'Sierra Leone': 'SLE',
    'Liberia': 'LRD',
    'Guinea': 'GNF',
    'United Kingdom': 'GBP',
    'Canada': 'CAD',
}

const Register = () => {
    const { register } = useAuth()

    const [accountType, setAccountType] = useState('user')
    const [loading, setLoading] = useState(false)
    const [alert, setAlert] = useState(null)
    const [serviceCategories,setServiceCategories]=useState([])

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        city: '',
        address: '',
        postalCode: '',
        businessName: '',
        businessEmail: '',
        businessPhone: '',
        country: 'United States',
        currency: 'USD',
        language: 'English',
        password: '',
        merchantType: 'standard',
        serviceCategoryIds: [],
        serviceProBio: '',
        serviceProTermsAccepted: false,
    })

    useEffect(()=>{if(accountType!=='merchant'||formData.merchantType!=='service_pro')return;let active=true;api.get('/service-pros/categories').then(r=>{if(active)setServiceCategories(r.data?.data||[])}).catch(()=>{if(active)setAlert({type:'error',message:'Service categories could not be loaded. Please try again.'})});return()=>{active=false}},[accountType,formData.merchantType])

    const handleChange = (field) => (event) => {
        const value = event.target.value

        if (field === 'country') {
            const autoCurrency = countryCurrencyMap[value] || 'USD'
            setFormData({
                ...formData,
                country: value,
                currency: autoCurrency,
            })
            return
        }

        setFormData({ ...formData, [field]: value })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setAlert(null)
        setLoading(true)

        try {
            await register({
                ...formData,
                role: accountType,
                email: accountType === 'merchant' ? formData.businessEmail : formData.email,
                phone: accountType === 'merchant' ? formData.businessPhone : formData.phone,
            })
        } catch (error) {
            setAlert({
                type: 'error',
                message:
                    error?.response?.data?.message ||
                    'Registration failed',
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#0b1220',
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    sx={{
                        p: 4,
                        borderRadius: 4,
                        bgcolor: '#0f1c2e',
                        color: '#fff',
                    }}
                >
                    {/* Title */}
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                        Sign Up
                    </Typography>

                    {/* Dynamic Instruction Text */}
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 3,
                            color: 'rgba(255,255,255,0.65)',
                        }}
                    >
                        {accountType === 'user'
                            ? 'Type your name exactly as it appears on your ID card.'
                            : 'Business name must exactly match your government-issued registration documents.'}
                    </Typography>

                    {/* Toggle */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <ToggleButtonGroup
                            value={accountType}
                            exclusive
                            onChange={(e, value) => {
                                if (value) setAccountType(value)
                            }}
                            sx={{
                                background: '#16263f',
                                borderRadius: 3,
                                p: 0.5,
                            }}
                        >
                            <ToggleButton
                                value="user"
                                sx={{
                                    textTransform: 'none',
                                    px: 3,
                                    borderRadius: 2,
                                    color: accountType === 'user' ? '#000' : '#fff',
                                    bgcolor:
                                        accountType === 'user'
                                            ? '#ff7a00'
                                            : 'transparent',
                                }}
                            >
                                User Account
                            </ToggleButton>

                            <ToggleButton
                                value="merchant"
                                sx={{
                                    textTransform: 'none',
                                    px: 3,
                                    borderRadius: 2,
                                    color:
                                        accountType === 'merchant'
                                            ? '#000'
                                            : '#fff',
                                    bgcolor:
                                        accountType === 'merchant'
                                            ? '#ff7a00'
                                            : 'transparent',
                                }}
                            >
                                Merchant Account
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {alert && (
                        <Alert severity={alert.type} sx={{ mb: 2 }}>
                            {alert.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* USER FORM */}
                        {accountType === 'user' && (
                            <>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    sx={{ mb: 2 }}
                                    value={formData.fullName}
                                    onChange={handleChange('fullName')}
                                />

                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    sx={{ mb: 2 }}
                                    value={formData.email}
                                    onChange={handleChange('email')}
                                />

                                <TextField
                                    fullWidth
                                    label="Cell Phone"
                                    sx={{ mb: 2 }}
                                    value={formData.phone}
                                    onChange={handleChange('phone')}
                                />

                                <TextField
                                    fullWidth
                                    label="City"
                                    sx={{ mb: 2 }}
                                    value={formData.city}
                                    onChange={handleChange('city')}
                                />

                                <TextField
                                    fullWidth
                                    label="Address Line 1"
                                    sx={{ mb: 2 }}
                                    value={formData.address}
                                    onChange={handleChange('address')}
                                />

                                <TextField
                                    fullWidth
                                    label="Postal / Zip Code (Optional)"
                                    sx={{ mb: 2 }}
                                    value={formData.postalCode}
                                    onChange={handleChange('postalCode')}
                                />
                            </>
                        )}

                        {/* MERCHANT FORM */}
                        {accountType === 'merchant' && (
                            <>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Business Type</InputLabel>
                                    <Select value={formData.merchantType} onChange={handleChange('merchantType')} label="Business Type">
                                        <MenuItem value="standard">Merchant / Business</MenuItem>
                                        <MenuItem value="service_pro">Service Pro</MenuItem>
                                    </Select>
                                </FormControl>
                                {formData.merchantType === 'service_pro' && <Alert severity="info" sx={{mb:2}}>Use the same merchant account, wallet, identity review, and approval process. Your Service Pro profile becomes searchable after approval.</Alert>}
                                <TextField
                                    fullWidth
                                    label="Business Name"
                                    sx={{ mb: 2 }}
                                    value={formData.businessName}
                                    onChange={handleChange('businessName')}
                                />

                                <TextField
                                    fullWidth
                                    label="Business Email"
                                    type="email"
                                    sx={{ mb: 2 }}
                                    value={formData.businessEmail}
                                    onChange={handleChange('businessEmail')}
                                />

                                <TextField
                                    fullWidth
                                    label="Business Cell Phone Number"
                                    sx={{ mb: 2 }}
                                    value={formData.businessPhone}
                                    onChange={handleChange('businessPhone')}
                                />

                                <TextField
                                    fullWidth
                                    label="City"
                                    sx={{ mb: 2 }}
                                    value={formData.city}
                                    onChange={handleChange('city')}
                                />

                                <TextField
                                    fullWidth
                                    label="Business Address Line 1"
                                    sx={{ mb: 2 }}
                                    value={formData.address}
                                    onChange={handleChange('address')}
                                />

                                <TextField
                                    fullWidth
                                    label="Postal / Zip Code"
                                    sx={{ mb: 2 }}
                                    value={formData.postalCode}
                                    onChange={handleChange('postalCode')}
                                />
                                {formData.merchantType === 'service_pro' && <>
                                    <FormControl fullWidth sx={{mb:2}}>
                                        <InputLabel>Services Offered</InputLabel>
                                        <Select multiple value={formData.serviceCategoryIds} onChange={handleChange('serviceCategoryIds')} label="Services Offered" renderValue={ids=>ids.map(id=>serviceCategories.find(c=>c.id===id)?.name).filter(Boolean).join(', ')}>
                                            {serviceCategories.map(category=><MenuItem key={category.id} value={category.id}><Checkbox checked={formData.serviceCategoryIds.includes(category.id)}/>{category.name}</MenuItem>)}
                                        </Select>
                                        <FormHelperText>Select one or more services your business provides.</FormHelperText>
                                    </FormControl>
                                    <TextField fullWidth multiline minRows={4} label="About your services" value={formData.serviceProBio} onChange={handleChange('serviceProBio')} sx={{mb:2}} helperText="Describe your experience and the work you provide. Do not include personal payment details."/>
                                    <FormControlLabel sx={{alignItems:'flex-start',mb:2}} control={<Checkbox required checked={formData.serviceProTermsAccepted} onChange={e=>setFormData({...formData,serviceProTermsAccepted:e.target.checked})}/>} label="I agree that payments for jobs obtained through MojoPayr must be completed through MojoPayr. I understand that requesting or accepting payment outside MojoPayr may lead to account suspension or removal."/>
                                </>}
                            </>
                        )}

                        {/* Shared Fields */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={formData.country}
                                onChange={handleChange('country')}
                                label="Country"
                            >
                                {Object.keys(countryCurrencyMap).map((country) => (
                                    <MenuItem key={country} value={country}>
                                        {country}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Currency (Auto Assigned)"
                            value={formData.currency}
                            disabled
                            sx={{ mb: 2 }}
                        />

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Language</InputLabel>
                            <Select
                                value={formData.language}
                                onChange={handleChange('language')}
                                label="Language"
                            >
                                <MenuItem value="English">English</MenuItem>
                                <MenuItem value="French">French</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Password"
                            type="password"
                            sx={{ mb: 3 }}
                            value={formData.password}
                            onChange={handleChange('password')}
                        />

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                bgcolor: '#ff7a00',
                                py: 1.5,
                                borderRadius: 3,
                                '&:hover': { bgcolor: '#e86f00' },
                            }}
                        >
                            {accountType === 'merchant'
                                ? formData.merchantType === 'service_pro' ? 'Create Service Pro Account' : 'Create Merchant Account'
                                : 'Continue'}
                        </Button>
                    </form>
                </Paper>
            </Container>
        </Box>
    )
}

export default Register
